import React, { useEffect, useState } from 'react';
import PipelineShell from '../common/PipelineShell';
import FileTree from '../common/FileTree';
import useStageJob from '../common/useStageJob';
import AgentActivityGraph from '../common/AgentActivityGraph';
import {
  getProject,
  setStageData,
  patchStage,
  subscribe,
  buildStageContext,
} from '../../../services/pipelineProject.service';
import { runPipelineStage } from '../../../services/pipeline.service';
import '../Pipeline.css';

/**
 * CI/CD Stage
 *
 * Generates pipeline configuration files: GitHub Actions, Dockerfile,
 * docker-compose, AWS buildspec, and deploy scripts.
 */
function CICDStage() {
  const [project, setProject] = useState(getProject());
  const [cicdData, setCicdData] = useState(null);
  const [error, setError]      = useState(null);
  const [activity, setActivity] = useState([]);

  useEffect(() => {
    const p = getProject();
    if (p.stages?.cicd?.data) setCicdData(p.stages.cicd.data);
    return subscribe(setProject);
  }, []);

  const handleResult = (result) => {
    setCicdData(result || {});
    setStageData('cicd', result || {});
    setError(null);
  };

  const handleActivity = (acts) => setActivity(acts || []);
  const { polling, error: pollError, startPolling } = useStageJob(handleResult, setError, handleActivity);

  const handleGenerate = async () => {
    setError(null);
    const context = buildStageContext('cicd');
    const comment = project?.stages?.cicd?.comment || '';
    try {
      const res = await runPipelineStage('cicd', context, comment);
      if (res.status === 'accepted') {
        patchStage('cicd', { jobId: res.jobId });
        startPolling(res.jobId);
      } else if (res.status === 'completed') {
        handleResult(res.result);
        if (res.agentActivity) setActivity(res.agentActivity);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to generate CI/CD config.');
    }
  };

  const tests        = project?.stages?.tests?.data;
  const hasCicd      = Boolean(cicdData?.files?.length);
  const displayError = error || pollError;

  return (
    <PipelineShell
      stageId="cicd"
      description="Generate CI/CD pipeline definitions, Dockerfiles, and deployment automation scripts."
      canApprove={hasCicd}
      headerAction={
        <button
          className="pipeline-btn primary"
          onClick={handleGenerate}
          disabled={polling || !tests}
          title={!tests ? 'Complete the Tests stage first' : undefined}
        >
          {polling ? '⏳ Generating…' : hasCicd ? '↩ Regenerate' : '🔁 Generate CI/CD'}
        </button>
      }
    >
      {!tests && (
        <div className="pipeline-empty">
          <div className="pipeline-empty-icon">🔒</div>
          <p>Complete and approve the <strong>Tests</strong> stage first.</p>
        </div>
      )}

      {(polling || activity.length > 0) && (
        <AgentActivityGraph activity={activity} polling={polling} />
      )}

      {polling && (
        <div className="pipeline-loading">
          <div className="pipeline-spinner" />
          <p>Generating CI/CD pipeline configuration…</p>
        </div>
      )}

      {displayError && !polling && <div className="pipeline-error">{displayError}</div>}

      {hasCicd && !polling && (
        <>
          {/* Metadata badges */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
            <span className="pipeline-file-count-badge">{cicdData.files.length} files</span>
            {cicdData.deploymentStrategy && (
              <span className="pipeline-tag">{cicdData.deploymentStrategy} deployment</span>
            )}
            {Array.isArray(cicdData.environments) && cicdData.environments.map((e) => (
              <span key={e} className="pipeline-tag green">{e}</span>
            ))}
          </div>

          {Array.isArray(cicdData.pipelineStages) && cicdData.pipelineStages.length > 0 && (
            <div className="pipeline-card">
              <div className="pipeline-card-title">Pipeline Stages</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                {cicdData.pipelineStages.map((s, i) => (
                  <React.Fragment key={s}>
                    <span className="pipeline-tag">{s}</span>
                    {i < cicdData.pipelineStages.length - 1 && (
                      <span style={{ color: '#94a3b8' }}>→</span>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}

          <FileTree files={cicdData.files} />
        </>
      )}
    </PipelineShell>
  );
}

export default CICDStage;
